import { useState } from 'react';
import { InitialsAvatar } from '@/client/components/common/InitialsAvatar';
import { Button } from '@/client/components/ui/button';
import { Card } from '@/client/components/ui/card';
import { cn } from '@/client/lib/utils';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { TYPE_META } from './constants';

type TimeOffRequest = RouterOutputs['timeoff']['list'][number];
type BalanceItem = RouterOutputs['timeoff']['myBalance'][number];

interface TimeOffRightRailProps {
  isAdmin: boolean;
  pendingRequests: TimeOffRequest[];
  myRequests: TimeOffRequest[];
  balance: BalanceItem[];
}

const STATUS_STYLE: Record<string, { label: string; className: string }> = {
  approved: { label: 'Approved', className: 'bg-green-100 text-green-700 border border-green-200' },
  pending:  { label: 'Pending',  className: 'bg-amber-100 text-amber-700 border border-amber-200' },
  rejected: { label: 'Declined', className: 'bg-red-100 text-red-700 border border-red-200' },
};

function fmtRange(from: string, to: string, days: number): string {
  const fmt = (s: string) => s.slice(5).replace('-', '/');
  return `${fmt(from)} – ${fmt(to)} · ${days} ${days === 1 ? 'day' : 'days'}`;
}

export function TimeOffRightRail({
  isAdmin,
  pendingRequests,
  myRequests,
  balance,
}: TimeOffRightRailProps) {
  const utils = trpc.useUtils();
  const [actionError, setActionError] = useState<string | null>(null);

  const approveMutation = trpc.timeoff.approve.useMutation({
    onSuccess: () => {
      utils.timeoff.list.invalidate();
      utils.timeoff.myBalance.invalidate();
      utils.timeoff.listPending.invalidate();
    },
    onError: e => setActionError(e.message),
  });

  const rejectMutation = trpc.timeoff.reject.useMutation({
    onSuccess: () => {
      utils.timeoff.list.invalidate();
      utils.timeoff.myBalance.invalidate();
      utils.timeoff.listPending.invalidate();
    },
    onError: e => setActionError(e.message),
  });

  return (
    <div className="flex min-h-0 flex-col gap-5 overflow-y-auto border-t p-6 lg:border-l lg:border-t-0">
      {/* My balance */}
      <div>
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          My balance · {new Date().getFullYear()}
        </p>
        <div className="flex flex-col gap-2.5">
          {balance.map(b => {
            const meta = TYPE_META[b.type];
            const pct = b.total > 0 ? Math.min((b.used / b.total) * 100, 100) : 0;
            const Icon = meta?.Icon;
            return (
              <Card key={b.type} className="p-3.5">
                <div className="mb-2.5 flex items-center gap-2.5">
                  {Icon && (
                    <div
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ background: meta.color + '22', color: meta.color }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="text-sm font-medium">{meta?.label ?? b.type}</div>
                    <div className="text-xs text-muted-foreground">{b.total - b.used} days left</div>
                  </div>
                  <div className="text-sm font-semibold tabular-nums">
                    {b.used}
                    <span className="font-normal text-muted-foreground"> / {b.total}</span>
                  </div>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, background: meta?.color ?? '#888' }}
                  />
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Approval queue — admin only */}
      {isAdmin && (
        <div>
          {actionError && (
            <p className="text-sm text-destructive">{actionError}</p>
          )}
          <div className="mb-3 flex items-center gap-2">
            <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
              Awaiting your approval
            </p>
            {pendingRequests.length > 0 && (
              <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
                {pendingRequests.length}
              </span>
            )}
          </div>
          {pendingRequests.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending requests</p>
          ) : (
            <div className="flex flex-col gap-2.5">
              {pendingRequests.map(r => {
                const meta = TYPE_META[r.type];
                const name = r.user.full_name ?? 'Unknown';
                return (
                  <Card key={r.id} className="p-3.5">
                    <div className="flex items-start gap-2.5">
                      <InitialsAvatar name={name} size={32} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold">{name}</div>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          <span
                            className="inline-block h-1.5 w-1.5 rounded-sm"
                            style={{ background: meta?.color }}
                          />
                          <span className="text-xs text-muted-foreground">
                            {meta?.label ?? r.type} · {r.days}{' '}
                            {r.days === 1 ? 'day' : 'days'}
                          </span>
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          {fmtRange(r.date_from, r.date_to, r.days)}
                        </div>
                        {r.note && (
                          <div className="mt-1 text-xs italic text-muted-foreground">
                            "{r.note}"
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1"
                        disabled={approveMutation.isPending}
                        onClick={() => approveMutation.mutate({ id: r.id })}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        disabled={rejectMutation.isPending}
                        onClick={() => rejectMutation.mutate({ id: r.id })}
                      >
                        Decline
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* My upcoming */}
      <div>
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
          My upcoming
        </p>
        {myRequests.length === 0 ? (
          <p className="text-sm text-muted-foreground">No upcoming requests</p>
        ) : (
          <div className="flex flex-col gap-2">
            {myRequests.map(r => {
              const meta = TYPE_META[r.type];
              const Icon = meta?.Icon;
              const statusStyle = STATUS_STYLE[r.status] ?? STATUS_STYLE.pending;
              return (
                <div
                  key={r.id}
                  className="flex items-center gap-2.5 rounded-lg border px-3 py-2.5"
                >
                  {Icon && (
                    <div
                      className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg"
                      style={{ background: meta.color + '22', color: meta.color }}
                    >
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{meta?.label ?? r.type}</div>
                    <div className="text-xs text-muted-foreground">
                      {fmtRange(r.date_from, r.date_to, r.days)}
                    </div>
                  </div>
                  <span
                    className={cn('rounded-full px-2 py-0.5 text-[11px] font-medium', statusStyle.className)}
                  >
                    {statusStyle.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
